/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ContactDetailsDialogConfig, contactDetailsDialogRoute } from '@gh/builder-shared';
import { BuilderPermission, ContactType } from '@gh/builder-shared-data';
import {
  createDialogRoute,
  DateTimeTypes,
  FormState,
  FormStateBuilder,
  getDialogInitData,
  UserContext,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { AutocompleteItem } from '@gh/common-shared-data';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  DialogService,
  FormMode,
  getControlValue,
  NotificationService,
  setControlValue,
  WaitingService,
} from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';

import { assign, isNil, keyBy, keys } from 'lodash';

import { ContactListItem, JobDetailsWarrantyTaskDialogConfig } from '../../../resources/job-details-tab';
import { JobDetailsTabBusinessService } from '../../../resources/job-details-tab/job-details-tab-business.service';
import { JobWarrantyTask } from '../../../resources/job-details-tab/job-details-tab-model';

@Component({
  selector: 'gh-job-details-warranty-task-details-dialog',
  template: require('./job-details-warranty-task-details-dialog.component.html'),
  styles: [require('./job-details-warranty-task-details-dialog.component.scss')],
})
export class JobDetailsWarrantyTaskDialogComponent implements OnInit, DialogInput<JobDetailsWarrantyTaskDialogConfig> {
  input: JobDetailsWarrantyTaskDialogConfig;
  form: FormState<JobWarrantyTask>;
  task: JobWarrantyTask;

  constructor(private dialogRef: MatDialogRef<any>,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private formStateBuilder: FormStateBuilder,
              private userContext: UserContext,
              private businessService: JobDetailsTabBusinessService,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private dialogs: DialogService) {

  }

  get dialogTitle(): string {
    return {
      [FormMode.CreateMode]: 'lbl_create_task',
      [FormMode.EditMode]: 'lbl_edit_task',
      [FormMode.ViewMode]: 'lbl_view_task',
    }[this.form.mode];
  }

  ngOnInit(): void {
    const { task } = getDialogInitData(this.dialogData);
    const basicInfo = this.input.basicInfo;

    assign(this, { task });

    this.form = this.formStateBuilder.buildPlain({
      value: task,
      mode: isNil(task.id)
        ? FormMode.CreateMode
        : this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT) && !basicInfo.isNotProceeding
          ? FormMode.EditMode
          : FormMode.ViewMode,
      enabled: whenCreateOrEditMode(),
      children: {
        supplier: { required: true },
        description: { required: true },
        enteredDate: { required: true, type: DateTimeTypes.ShortDate },
        toBeCompletedDate: { type: DateTimeTypes.ShortDate },
        completedDate: { type: DateTimeTypes.ShortDate },
        comment: void 0,
      },
    });
  }

  loadContacts(event: AutocompleteLoadMoreEvent): void {
    event.load(this.businessService.getContacts(event.query));
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.form.group.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const save$ = this.form.createMode ? this.businessService.createWarrantyTask(this.form.value) :
      this.businessService.updateWarrantyTask({
        ...this.form.value,
        id: this.task.id,
      });

    save$.pipe(
      this.waitingService.operator())
      .subscribe(
        (task) => {
          this.notificationService.success(this.form.createMode
            ? 'msg_created_successfully'
            : 'msg_saved_successfully');
          this.dialogRef.close(task);
        },
        this.notificationService.processAll);
  }

  onOpenContact(): void {
    this.openContact(getControlValue(this.form.group, 'supplier'));
  }

  onCreateContact(): void {
    this.openContact();
  }

  getContactTypes(contact: ContactListItem): ContactType[] {
    return <ContactType[]>keys(contact.types);
  }

  private getAvailableContactTypes(): ContactType[] {
    const types: ContactType[] = [];

    if (this.userContext.hasPermission(BuilderPermission.Contacts.CreditorSupplier.CREATE_EDIT)) {
      types.push(ContactType.CreditorSupplier);
    }

    if (this.userContext.hasPermission(BuilderPermission.Contacts.Employee.CREATE_EDIT)) {
      types.push(ContactType.Employee);
    }

    return types;
  }

  private openContact(openedContact?: AutocompleteItem): void {
    this.dialogs.openWithInput(contactDetailsDialogRoute, <ContactDetailsDialogConfig>{
      contactType: this.getAvailableContactTypes(),
      contactId: openedContact ? openedContact.id : void 0,
      parentTitle: this.dialogTitle,
      entryPoint: 'warranty',
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((contact: any) => {
        setControlValue(this.form.group, 'supplier', {
          id: contact.contactId,
          name: contact.name,
          types: keyBy(contact.types),
        });
      });
  }
}

export const jobDetailsWarrantyTaskDialogRoute = createDialogRoute({
  component: JobDetailsWarrantyTaskDialogComponent,
  init: {
    service: JobDetailsTabBusinessService,
    selector: 'initWarrantyTaskDialogView',
  },
  config: { autoFocus: false },
});
